/**
 * BankTab — tuile « Banque » (Batch #14.292 / #14.343) : choix du compte, puis
 *   Sommaire (Relevé bancaire | Grand livre, fiche de rapprochement, report, PDF) · Rapprochement (registre GL avec états) · Totaux
 *   · Relevé importé (lignes & correspondances) · Importer un relevé · Relevés importés · Règles.
 */
import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ArrowLeftRight, Landmark, Loader2 } from "lucide-react";
import { api } from "@/api";
import { toast } from "@/lib/toast";
import { Btn, Empty, errMsg, useGlBase } from "../../comptabilite/_shared";
import TransferDialog from "../../comptabilite/TransferDialog";
import BankSummary from "./BankSummary";
import BankImport from "./BankImport";
import BankLines from "./BankLines";
import BankStatements from "./BankStatements";
import BankRules from "./BankRules";
import ReconSummary from "./ReconSummary";
import ReconRegister from "./ReconRegister";
import ReconTotals from "./ReconTotals";

const SUBTABS = [["sommaire", "Sommaire"], ["rapprochement", "Rapprochement"], ["totaux", "Totaux"], ["lines", "Relevé importé"], ["import", "Importer un relevé"], ["statements", "Relevés importés"], ["rules", "Règles"]];
const IMPORT_TABS = new Set(["lines", "import", "statements", "rules"]);

export default function BankTab({ accounts, settings, refresh, goTab }) {
  const base = useGlBase();
  const [sp, setSp] = useSearchParams();
  const sub = SUBTABS.some(([id]) => id === sp.get("tab")) ? sp.get("tab") : "sommaire";
  const [bankAccounts, setBankAccounts] = useState(null);
  const [accountId, setAccountId] = useState(sp.get("account") || "");
  const [summary, setSummary] = useState(null);
  const [recon, setRecon] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [transfer, setTransfer] = useState(false);

  const setSub = (id) => { const n = new URLSearchParams(sp); n.set("tab", id); setSp(n, { replace: true }); };

  const loadAccounts = useCallback(async () => {
    try {
      const r = await api.get(`${base}/bank/accounts`);
      setBankAccounts(r.data.items || []);
      setAccountId((cur) => cur && r.data.items.some((a) => a.id === cur) ? cur : (r.data.items.find((a) => a.role === "bank") || r.data.items[0])?.id || "");
    } catch (e) { toast.error(errMsg(e, "Chargement des comptes impossible")); setBankAccounts([]); }
  }, [base]);
  useEffect(() => { loadAccounts(); }, [loadAccounts]);

  const loadRecon = useCallback(async () => {
    if (!accountId) return;
    try {
      const r = await api.get(`${base}/bank/reconciliation`, { params: { account_id: accountId } });
      setRecon(r.data);
      const s = await api.get(`${base}/bank/summary`, { params: { account_id: accountId, as_of: r.data.reconciliation.recon_date } });
      setSummary(s.data);
    } catch (e) { toast.error(errMsg(e, "Sommaire indisponible")); }
  }, [base, accountId]);
  useEffect(() => { loadRecon(); }, [loadRecon, reloadKey]);

  const changed = () => { setReloadKey((k) => k + 1); loadAccounts(); refresh(); };

  if (bankAccounts === null) return <div className="p-10 text-center text-slate-400"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>;
  if (!accounts.length) return <div className="bg-white border rounded-xl"><Empty icon={Landmark} text="Installez d'abord le plan comptable (tuile Plan comptable) pour utiliser la conciliation bancaire." testId="bank-no-accounts" /></div>;
  if (!bankAccounts.length) return (
    <div className="bg-white border rounded-xl" data-testid="bank-no-flagged">
      <Empty icon={Landmark} text="Aucun compte n'est coché « Compte bancaire à rapprocher ». Ouvrez le compte d'encaisse, chaque compte de banque, la carte de crédit ou le compte Stripe dans le Plan comptable et cochez la case.">
        <Btn tone="slate" onClick={() => goTab?.("plan")} testId="bank-go-plan">Ouvrir le plan comptable</Btn>
      </Empty>
    </div>
  );

  const asOf = recon?.reconciliation?.recon_date;
  const acc = bankAccounts.find((a) => a.id === accountId);
  const common = { base, accountId, accounts, settings, bankAccounts, summary, changed, goTab };
  return (
    <div className="space-y-4" data-testid="bank-tab">
      <div className="flex items-center gap-2 flex-wrap">
        <label className="text-xs font-semibold text-slate-600">Code banque</label>
        <select value={accountId} onChange={(e) => { setAccountId(e.target.value); setRecon(null); const n = new URLSearchParams(sp); n.set("account", e.target.value); setSp(n, { replace: true }); }}
          className="border border-slate-300 rounded-lg px-2 py-1.5 text-sm bg-white min-w-[220px]" data-testid="bank-account-select">
          {bankAccounts.map((a) => <option key={a.id} value={a.id}>{`${a.number} — ${a.name}${a.unmatched ? ` · ${a.unmatched} à concilier` : ""}`}</option>)}
        </select>
        {acc && <span className="text-xs text-slate-500" data-testid="bank-account-caption">N° compte GL <strong className="font-mono">{acc.number}</strong>{acc.role ? ` · rôle ${acc.role === "bank" ? "Banque" : acc.role === "credit_card" ? "Carte de crédit" : "Stripe"}` : ""}</span>}
        {asOf && <span className="text-xs text-slate-500 ml-2" data-testid="bank-recon-date-caption">Rapprochement au <strong>{asOf}</strong></span>}
        <span className="text-[10px] text-slate-400 hidden sm:inline" data-testid="bank-flag-hint">Seuls les comptes cochés « à rapprocher » (Plan comptable) sont listés.</span>
        <div className="ml-auto">
          <Btn tone="slate" icon={ArrowLeftRight} onClick={() => setTransfer(true)} disabled={bankAccounts.length < 2} testId="bank-transfer-btn" title="Virement entre deux comptes bancaires — l'écriture apparaît dans les deux rapprochements">Virement</Btn>
        </div>
      </div>

      {IMPORT_TABS.has(sub) && <BankSummary summary={summary} goTab={goTab} onImport={() => setSub("import")} />}

      <div className="flex gap-1 border-b overflow-x-auto">
        {SUBTABS.map(([id, label]) => (
          <button key={id} onClick={() => setSub(id)} className={`px-4 py-2 text-sm border-b-2 transition whitespace-nowrap ${sub === id ? "border-teal-600 text-teal-700 font-semibold" : "border-transparent text-gray-500 hover:text-gray-700"}`} data-testid={`bank-subtab-${id}`}>
            {label}
            {id === "lines" && summary?.counts?.unmatched > 0 && <span className="ml-1.5 text-[10px] font-bold bg-amber-100 text-amber-800 rounded-full px-1.5 py-0.5" data-testid="bank-unmatched-badge">{summary.counts.unmatched}</span>}
            {id === "rapprochement" && recon?.summary?.totals?.outstanding?.count > 0 && <span className="ml-1.5 text-[10px] font-bold bg-amber-100 text-amber-800 rounded-full px-1.5 py-0.5" data-testid="bank-outstanding-badge">{recon.summary.totals.outstanding.count}</span>}
          </button>
        ))}
      </div>

      {sub === "sommaire" && (recon ? <ReconSummary base={base} accountId={accountId} accounts={accounts} recon={recon} reloadRecon={loadRecon} onOpenRegister={() => setSub("lines")} /> : <div className="p-10 text-center text-slate-400"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>)}
      {sub === "rapprochement" && <ReconRegister base={base} accountId={accountId} asOf={asOf} reloadKey={reloadKey} changed={changed} goTab={setSub} />}
      {sub === "totaux" && <ReconTotals summary={recon?.summary} />}
      {sub === "lines" && <BankLines {...common} reloadKey={reloadKey} />}
      {sub === "import" && <BankImport {...common} onImported={() => { changed(); setSub("lines"); }} />}
      {sub === "statements" && <BankStatements {...common} />}
      {sub === "rules" && <BankRules {...common} />}
      {transfer && <TransferDialog defaultFrom={accountId} onClose={() => setTransfer(false)} onSaved={() => { setTransfer(false); changed(); }} />}
    </div>
  );
}
